const WINDOW_SCROLL_OFFSET = 100;
const OVERLAY_SCROLL_OFFSET = 56;

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);
  return overflowY === 'auto' || overflowY === 'scroll';
};

const findScrollContainer = (element: HTMLElement): HTMLElement | Window => {
  const overlayParent = element.closest('.overlay-content');
  if (overlayParent instanceof HTMLElement) {
    return overlayParent;
  }

  let current: HTMLElement | null = element.parentElement;
  while (current) {
    if (isScrollable(current)) {
      return current;
    }
    current = current.parentElement;
  }

  return window;
};

const getTargetTop = (container: HTMLElement | Window, element: HTMLElement, offset: number) => {
  if (container instanceof Window) {
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    return Math.max(elementTop - offset, 0);
  }

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const relativeTop = elementRect.top - containerRect.top;
  return Math.max(container.scrollTop + relativeTop - offset, 0);
};

export const scrollElementIntoView = (
  element: HTMLElement,
  options?: { offset?: number },
) => {
  if (typeof window === 'undefined') return;

  const container = findScrollContainer(element);
  const defaultOffset =
    container instanceof Window ? WINDOW_SCROLL_OFFSET : OVERLAY_SCROLL_OFFSET;
  const offset = options?.offset ?? defaultOffset;
  const targetTop = getTargetTop(container, element, offset);

  if (container instanceof Window) {
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  } else {
    container.scrollTo({ top: targetTop, behavior: 'smooth' });
  }
};

export const scrollToId = (id: string, options?: { offset?: number }) => {
  if (typeof document === 'undefined') return false;
  const element = document.getElementById(id);
  if (!element) return false;

  scrollElementIntoView(element, options);
  return true;
};

export const scrollToHash = (hash?: string | null, options?: { offset?: number }) => {
  if (!hash) return false;
  const cleanHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!cleanHash) return false;

  return scrollToId(cleanHash, options);
};
